import { RotateCcw, Volume2, VolumeX } from "lucide-react";
import { useVirtualHr } from "../lib/virtualHr.js";
import type { VirtualHrGuide as Guide } from "../hooks/useVirtualHrGuide.js";
import { Button } from "./ui/button.js";
import { GlowCard } from "./ui/glow-card.js";
import { Tooltip } from "./ui/tooltip.js";
import { AIOrb } from "./ui/ai-orb/AIOrb.js";

const GREETING = "Hello! I'll be guiding you through your onboarding.";

/**
 * Sirra, the virtual HR assistant, on the onboarding wizard: a speaking guide whose orb shifts
 * through the GlowCard aurora colours with her voice, and whose caption types out as she talks.
 * It stays pinned to the top of the screen while the form scrolls underneath.
 * Hidden when the Super HR Admin has switched Virtual HR off.
 */
export function VirtualHrGuide({ guide }: { guide: Guide }) {
  const { contextEnabled, assistantName } = useVirtualHr();
  // The guide only exists in onboarding: it is gone when the admin turns Onboarding (or Sirra as a whole) off.
  if (!contextEnabled("onboarding")) return null;

  const full = guide.caption || GREETING;
  const typed = guide.caption ? guide.typed : full; // before her first line, the greeting is shown whole
  const typing = typed.length < full.length;
  const lastWord = typed.search(/\S+$/); // start of the word being typed, -1 when the text ends in a space
  const wordStart = lastWord === -1 ? typed.length : lastWord;

  return (
    // Pinned, so it never scrolls away; the aurora border sweeps each time she starts a new line.
    <GlowCard
      as="section"
      interaction="both"
      sweepKey={full}
      size="none"
      glow="medium"
      spotlight="low"
      aria-label={`${assistantName}, your virtual HR assistant`}
      className="sticky top-[calc(var(--wizard-bar-h,0px)+0.375rem)] z-10 mb-4 flex items-center gap-3 bg-surface-0/90 px-3 py-2 pr-4 backdrop-blur-md"
    >
      {/* The canvas is transparent: the orb sits straight on the card, so it matches any theme. */}
      <div className="shrink-0">
        <AIOrb state={guide.orbState} audioAnalyser={guide.orbAnalyser} size={96} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[14px] font-bold text-surface-900">{assistantName}</p>
          <span className="text-[11.5px] text-surface-500">{guide.speaking ? "Speaking…" : guide.buffering ? "Loading voice…" : "Your virtual HR assistant"}</span>
        </div>

        {/* The full line is laid out (invisibly) so the card never changes height while it types. */}
        <div className="relative mt-1 text-[13.5px] leading-[20px] text-surface-700">
          <p className="invisible" aria-hidden>{full}</p>
          <p className="absolute inset-0" aria-hidden>
            {typing ? (
              <>
                {typed.slice(0, wordStart)}
                {/* The word being typed glows in the aurora colours; once the line is done it turns back to normal. */}
                <span className="bg-gradient-to-r from-[var(--aurora-1)] via-[var(--aurora-2)] to-[var(--aurora-3)] bg-clip-text text-transparent">{typed.slice(wordStart)}</span>
              </>
            ) : typed}
            {typing && (guide.speaking || guide.typed.length > 0) && <span className="ml-0.5 inline-block h-[1.05em] w-[2px] translate-y-[3px] bg-[var(--aurora-1)] motion-safe:animate-pulse" />}
          </p>
          {/* Typing character by character would flood a screen reader, so it hears the whole line once. */}
          <p className="sr-only" role="status" aria-live="polite">{full}</p>
        </div>

        {guide.supported && guide.waitingForTap && !guide.muted && (
          <p className="mt-1 text-[12px] font-semibold text-[var(--aurora-1)]">Click anywhere on the page and I'll start talking.</p>
        )}
      </div>

      {guide.supported && (
        <div className="flex shrink-0 items-center gap-1">
          <Tooltip content="Say it again" side="top">
            <Button size="icon" variant="ghost" aria-label="Replay" onClick={guide.replay} disabled={!guide.caption || guide.muted}><RotateCcw size={15} /></Button>
          </Tooltip>
          <Tooltip content={guide.muted ? "Turn voice on" : "Mute voice"} side="top">
            <Button size="icon" variant="ghost" aria-label={guide.muted ? "Turn voice on" : "Mute voice"} aria-pressed={guide.muted} onClick={guide.toggleMute}>
              {guide.muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </Button>
          </Tooltip>
        </div>
      )}
    </GlowCard>
  );
}
